// src/pages/Admin/Step2Merchant.js (Create new file)

import React, { useState, useEffect } from "react";
import { Form, Formik, FieldArray } from "formik";
import * as Yup from "yup";
import isEqual from 'lodash/isEqual';
import {
    Box, Button, Typography, TextField,
    FormControlLabel,
    Checkbox,
    Autocomplete,
    Grid,
} from "@mui/material";
import BankAccountForm from "./BankAccountForm";
import MerchantForm from "./MerchantForm";
import {
    merchantAction,
    Log,
    sendDataWithGet,
    sendDataWithPost,
} from "../../services/services";
import { styles } from "../../utils/styles";

const DRAFT_KEY = 'onboarding_draft';

const loadStepData = (stepIndex) => {
    try {
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data.allFormData ? data.allFormData[stepIndex] : null;
    } catch (e) {
        Log.error('Failed to load step data from storage:', e);
        return null;
    }
};

const getMerchantValidationSchema = (numMerchants) => {
    // Create Schema for each Merchant in the 'items' array
    const merchantSchema = Yup.object().shape({
        name: Yup.string().required("Merchant name is required"),
        number_of_terminals: Yup.number()
            .typeError("Number of terminals must be a number")
            .required("Number of terminals is required")
            .min(1, "Must have at least 1 terminal")
            .integer("Must be a whole number"),
        bank_account: Yup.object().shape({
            account_number: Yup.string().required("Account number is required"),
            account_name: Yup.string().required("Account name is required"),
            bank: Yup.string().required("Bank name is required"),
            // bank_address: Yup.string().required("Bank address is required"),
            bank_country: Yup.string().required("Bank country is required"),
            // bank_branch_code: Yup.string().required("Bank branch code is required"),
            bic_swift_code: Yup.string().required("BIC/SWIFT code is required"),
            iban: Yup.string().when('bank_country', {
                is: (val) => val && val.toUpperCase() !== 'SINGAPORE',
                then: (schema) => schema.required("IBAN is required"),
                otherwise: (schema) => schema
            }),
            // bank_transfer_fee: Yup.number().required("Bank transfer fee is required"),
        }),
        // Note: Address fields will have their own logic if `address_as_business_entity` is false
    });

    return Yup.object().shape({
        // 'items' is an array of Merchants
        items: Yup.array().of(merchantSchema)
    });
};



const EditStep2Merchant = ({ onboardingAppId, numMerchants, onNext, onBack, isSubmitting, business_entity_id, activeBusinessEntity, userOxygen7Entity = null, readOnly = false, onSkip }) => {
    const [staticList, setStaticList] = useState({});

    // Static data loading (Merchant list, Payout Cycle list, MCC list)
    useEffect(() => {
        const url = "/bank_account/merchant_payout_info/static";
        sendDataWithGet(url, setStaticList);
    }, []);

    // Load data from localStorage
    const loadedData = loadStepData(1);

    // Create an empty initial values ​​array with the correct number of Merchants
    const initialMerchants = Array.from({ length: numMerchants }, (_, index) => {
        const existingData = loadedData?.items?.[index];

        // Helper to determine payment_cycle structure
        let paymentCycleValue = existingData?.payment_cycle || '';
        if (typeof paymentCycleValue === 'object' && paymentCycleValue !== null) {
            // If it's the { id, label } object structure from draft
            paymentCycleValue = paymentCycleValue;
        } else if (existingData?.payment_cycle_id && existingData?.payment_cycle_label) {
            // Fallback: if we were storing id and label separately (not used in your draft, but good for robustness)
            paymentCycleValue = { id: existingData.payment_cycle_id, label: existingData.payment_cycle_label };
        } else if (existingData?.payment_cycle) {
            // Handle if it's just the ID/string from a simple initial load
            paymentCycleValue = existingData.payment_cycle;
        }

        return {
            // Needs to match MerchantPendingSerializer data structure
            id: existingData?.id || '', // Include ID for existing merchants
            name: existingData?.name || '',
            merchant_category_code: existingData?.merchant_category_code || '', // This is the ID
            address_as_business_entity: existingData?.address_as_business_entity || false,
            can_void: existingData?.can_void || false,
            can_refund: existingData?.can_refund || false,
            oxygen7_entity: existingData?.oxygen7_entity
                ? { id: existingData.oxygen7_entity?.id, label: existingData.oxygen7_entity?.entity ?? existingData.oxygen7_entity?.label }
                : (userOxygen7Entity || null),
            bank_account: existingData?.bank_account?.account_number !== undefined
                ? existingData.bank_account
                : {
                    account_number: existingData?.bank_account?.account_number || '',
                    account_name: existingData?.bank_account?.account_name || '',
                    bank: existingData?.bank_account?.bank || '',
                    bank_address: existingData?.bank_account?.bank_address || '',
                    bank_country: existingData?.bank_account?.bank_country || '',
                    bank_branch_code: existingData?.bank_account?.bank_branch_code || '',
                    bic_swift_code: existingData?.bank_account?.bic_swift_code || '',
                    iban: existingData?.bank_account?.iban || '',
                    bank_transfer_fee: existingData?.bank_account?.bank_transfer_fee || '',
                    beneficiary_name: existingData?.bank_account?.beneficiary_name || '',
                    beneficiary_address: existingData?.bank_account?.beneficiary_address || '',
                    beneficiary_bank_code: existingData?.bank_account?.beneficiary_bank_code || '',
                    beneficiary_branch_code: existingData?.bank_account?.beneficiary_branch_code || '',
                    intermediary_bank: existingData?.bank_account?.intermediary_bank || '',
                    intermediary_bank_code: existingData?.bank_account?.intermediary_bank_code || '',
                    intermediary_name: existingData?.bank_account?.intermediary_name || '',
                    transfer_charge_pay_by: existingData?.bank_account?.transfer_charge_pay_by || '',
                    settlement_fee: existingData?.bank_account?.settlement_fee || '',
                },
            payment_cycle: paymentCycleValue, // Use the determined structure
            min_payout_amount: existingData?.min_payout_amount || [],
            number_of_terminals: existingData?.number_of_terminals || '',
            average_ticket_size: existingData?.average_ticket_size || '',
            average_ticket_size_currency: existingData?.average_ticket_size_currency || 'SGD',
            address_line1: existingData?.address_line1 || '',
            address_line2: existingData?.address_line2 || '',
            address_line3: existingData?.address_line3 || '',
            city: existingData?.city || '',
            postal_code: existingData?.postal_code || '',
            // ... other fields
        }
    });

    const initialValues = loadedData && loadedData.items?.length === numMerchants ? loadedData : { items: initialMerchants };

    // Ensure we have the correct number of merchants in the form data
    const [currentValues, setCurrentValues] = useState(initialValues);

    useEffect(() => {
        if (currentValues.items && currentValues.items.length !== numMerchants) {
            // If the number of merchants changed, update the form data
            const updatedItems = Array.from({ length: numMerchants }, (_, index) => ({
                name: currentValues.items?.[index]?.name || '',
                merchant_category_code: currentValues.items?.[index]?.merchant_category_code || '',
                address_as_business_entity: currentValues.items?.[index]?.address_as_business_entity || false,
                can_void: currentValues.items?.[index]?.can_void || false,
                can_refund: currentValues.items?.[index]?.can_refund || false,
                oxygen7_entity: currentValues.items?.[index]?.oxygen7_entity || '',
                bank_account: currentValues.items?.[index]?.bank_account || {},
                payment_cycle: currentValues.items?.[index]?.payment_cycle || '',
                minimum_payout_amount: currentValues.items?.[index]?.minimum_payout_amount || '',
                number_of_terminals: currentValues.items?.[index]?.number_of_terminals || '',
                address_line1: currentValues.items?.[index]?.address_line1 || '',
                address_line2: currentValues.items?.[index]?.address_line2 || '',
                address_line3: currentValues.items?.[index]?.address_line3 || '',
                city: currentValues.items?.[index]?.city || '',
                postal_code: currentValues.items?.[index]?.postal_code || '',
                // ... other fields
            }));
            setCurrentValues({ items: updatedItems });
        }
    }, [numMerchants, currentValues.items]);

    return (
        <Formik
            initialValues={currentValues}
            validationSchema={getMerchantValidationSchema(numMerchants)}
            onSubmit={(values, actions) => {
                if (isEqual(values, initialValues)) {
                    onSkip();
                } else {
                    // Ensure bank_account includes all fields from BankAccountForm
                    const allBankAccountFields = [
                        'account_number', 'account_name', 'bank', 'bank_address', 'bank_country',
                        'bank_branch_code', 'bic_swift_code', 'iban', 'bank_transfer_fee',
                        'beneficiary_name', 'beneficiary_address', 'beneficiary_bank_code',
                        'beneficiary_branch_code', 'intermediary_bank', 'intermediary_bank_code',
                        'intermediary_name', 'transfer_charge_pay_by', 'settlement_fee'
                    ];

                    const updatedValues = {
                        ...values,
                        items: values.items.map(merchant => ({
                            ...merchant,
                            bank_account: allBankAccountFields.reduce((acc, field) => {
                                acc[field] = merchant.bank_account?.[field] || '';
                                return acc;
                            }, {})
                        }))
                    };

                    onNext(updatedValues, actions); // Call handleNext of the parent component
                }
            }}
            enableReinitialize={true}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                <Form autoComplete="off" noValidate>
                    <Grid container spacing={4} sx={{ py: 2, px: 2 }}>
                        {/* <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Merchant Details (Step 2)</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                Please enter information for **{numMerchants}** Merchants.
                            </Typography>
                        </Grid> */}

                        <Grid item xs={12}>
                            <FieldArray name="items">
                                {({ push, remove }) => (
                                    <>
                                        {/* <Grid container spacing={2} className="testClass"> */}
                                        {values.items && values.items.length > 0 && (
                                            values.items.map((merchant, index) => (
                                                <>
                                                    <MerchantForm
                                                        key={index}
                                                        merchantIndex={index}
                                                        values={values}
                                                        handleChange={handleChange}
                                                        handleBlur={handleBlur}
                                                        errors={errors}
                                                        touched={touched}
                                                        setFieldValue={setFieldValue}
                                                        isSubmitting={isSubmitting}
                                                        staticList={staticList}
                                                        activeBusinessEntity={activeBusinessEntity}
                                                        userOxygen7Entity={userOxygen7Entity}
                                                        readOnly={readOnly}
                                                    />
                                                    <BankAccountForm
                                                        key={`bank-${index}`}
                                                        merchantIndex={index}
                                                        values={values}
                                                        handleChange={handleChange}
                                                        handleBlur={handleBlur}
                                                        errors={errors}
                                                        touched={touched}
                                                        setFieldValue={setFieldValue}
                                                        isSubmitting={isSubmitting}
                                                        readOnly={readOnly}
                                                    />
                                                </>
                                            ))
                                        )}
                                        {/* </Grid> */}
                                    </>
                                )}
                            </FieldArray>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, position: 'relative', zIndex: 11 }}>
                                <Button onClick={onBack} variant="outlined" disabled={isSubmitting || readOnly}>
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSubmitting || readOnly}
                                    sx={{ ...styles.customButton }}
                                >
                                    {isSubmitting ? 'Saving...' : 'Save & Continue'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    );
};

export default EditStep2Merchant;